import { useReactiveVar } from '@bestseller/retail-planning.utils.reactive-vars';
import { FilterType } from '@bestseller-bit/frontend-community.bootstrapping.application-shell';
import { useCallback, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useFilterContext } from 'src/contexts/FilterContext';
import { FilterOption, initialFilters } from 'src/domain';
import { selectedPartnerVar } from 'src/infrastructure/local_state';
import { useCurrentStore } from './store';
import { useQueryParams } from './useQueryParams';
import { useRedirectToOverview } from './useRedirectToOverview';

const createFilter = (items: FilterOption[], groupKey: string, groupName: string) =>
    items.map((item) => ({
        key: groupKey,
        value: item.value?.toString() || '',
        displayName: item.label,
        filterGroup: groupKey,
        name: groupName,
    }));

interface Props {
    filtersState: FilterType[];
    setFiltersState: (filters: FilterType[]) => void;
}
export const useSyncFilters = ({ filtersState, setFiltersState }: Props) => {
    const location = useLocation();
    const [filterContext, setFilterContext] = useFilterContext();
    const { redirectToOverview } = useRedirectToOverview();
    const [selectedPartner] = useReactiveVar(selectedPartnerVar);
    const selectedStore = useCurrentStore();
    const { storeId } = useQueryParams();

    const isOnFrontpage = location.pathname === '/';

    const hasMeaningfulFilters = useMemo(() => {
        return (
            filterContext.chains.length > 0 ||
            filterContext.countries.length > 0 ||
            filterContext.clusters.length > 0 ||
            filterContext.partners.length > 0
        );
    }, [filterContext]);

    const frontpageFilters: FilterType[] = useMemo(() => {
        return [
            ...createFilter(filterContext.chains, 'chains', 'Chains'),
            ...createFilter(filterContext.countries, 'countries', 'Countries'),
            ...createFilter(filterContext.clusters, 'clusters', 'Clusters'),
            ...createFilter(filterContext.partners, 'partners', 'Partners'),
        ];
    }, [filterContext]);

    const clearFrontPageFilters = useCallback(() => {
        setFilterContext(initialFilters);
    }, [setFilterContext]);

    const clearStorePageFilters = useCallback(() => {
        if (selectedPartner) {
            setFiltersState([
                {
                    displayName: selectedPartner.name ?? '',
                    filterGroup: 'partner',
                    key: 'partner',
                    name: 'Partner',
                    value: selectedPartner.compositeId?.toString() ?? '',
                },
            ]);
            redirectToOverview();
        }
    }, [redirectToOverview, selectedPartner, setFiltersState]);

    useEffect(() => {
        if (isOnFrontpage && filtersState.length === 0 && hasMeaningfulFilters) {
            setFiltersState(frontpageFilters);
        }
    }, [isOnFrontpage, filtersState.length, hasMeaningfulFilters, frontpageFilters, setFiltersState]);

    useEffect(() => {
        if (!isOnFrontpage && filtersState.length === 0) {
            const newFilters = [];

            if (selectedPartner && (storeId === 'overview' || (storeId !== 'overview' && selectedStore))) {
                newFilters.push({
                    displayName: selectedPartner.name ?? '',
                    filterGroup: 'partner',
                    key: 'partner',
                    name: 'Partner',
                    value: selectedPartner.compositeId?.toString() ?? '',
                });

                if (selectedStore) {
                    newFilters.push({
                        displayName: selectedStore.storeName ?? '',
                        filterGroup: 'stores',
                        key: 'stores',
                        name: 'Stores',
                        value: selectedStore.id.toString(),
                    });
                }
                setFiltersState(newFilters);
            }
        }
    }, [selectedPartner, selectedStore, storeId, filtersState, setFiltersState, isOnFrontpage]);

    const clearFilters = useCallback(() => {
        if (isOnFrontpage) {
            clearFrontPageFilters();
        } else {
            clearStorePageFilters();
        }
    }, [isOnFrontpage, clearFrontPageFilters, clearStorePageFilters]);

    return { clearFilters };
};
